import { createContext, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "./api";

export interface Me {
  user: { id: string; email: string; displayName: string; roles: string[]; permissions: string[]; mustChangePassword: boolean };
  identity: { id: string; displayName: string; email?: string; managerId?: string; orgUnitId?: string; status: string } | null;
  isManager: boolean;
  tenant: { slug: string; displayName: string; version?: string };
  /** Behaviour gated on the tenant's platform version (server: versionGate.ts). */
  features?: { nhi: boolean; onboarding?: boolean; basicConfig?: boolean; peopleFromAccounts?: boolean; personMappingDefaults?: boolean };
}

export function hasPermission(perms: string[] | undefined, required: string): boolean {
  if (!perms) return false;
  const [area, verb] = required.split(":");
  return perms.some((g) => {
    if (g === "*" || g === required) return true;
    const [ga, gv] = g.split(":");
    return (ga === area && gv === "*") || (ga === "*" && gv === verb);
  });
}

/** Tenant-side session. `mePath` differs for the hosting admin app. */
export function useMe<T = Me>(mePath = "/api/auth/me") {
  return useQuery<T | null, ApiError>({
    queryKey: ["me", mePath],
    queryFn: async () => { try { return await apiFetch<T>(mePath); } catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; } },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogout(logoutPath = "/api/auth/logout") {
  const qc = useQueryClient();
  return async () => { await apiFetch(logoutPath, { method: "POST" }); qc.clear(); window.location.href = "/"; };
}

export const MeContext = createContext<Me | null>(null);
export const useCurrentUser = () => useContext(MeContext);
